import React, { lazy } from 'react';
import { Button } from '../components/ui/button';
import { LogoFull } from '../components/Logo';
import { SEO } from '../components/SEO';
import { useLanguage } from '../lib/LanguageContext';
import { ArrowLeft, MapPin, MessageCircle, Mail, Clock, ExternalLink, Phone, Building2 } from 'lucide-react';
import { Footer } from '../components/Footer';
import { ScrollToTop } from '../components/ScrollToTop';
interface KontakPageProps {
  onBack: () => void;
}
export function KontakPage({
  onBack
}: KontakPageProps) {
  const {
    t,
    lang,
    setLang
  } = useLanguage();
  const k = t.kontak;
  return <div className="min-h-screen bg-white font-sans selection:bg-brand-green/20 flex flex-col" data-id="element-492">
      <SEO page="kontak" data-id="element-493" />

      {/* Top Bar */}
      <div className="sticky top-0 z-50 bg-white/95 backdrop-blur-md border-b border-forest-100 shadow-sm" data-id="element-494">
        <div className="container mx-auto px-4 sm:px-6 md:px-8 py-4 flex items-center justify-between gap-3" data-id="element-495">
          <div className="flex items-center gap-3" data-id="element-496">
            <button onClick={onBack} className="group flex items-center gap-2 text-forest-500 hover:text-forest-900 text-sm font-medium transition-colors px-3 py-1.5 rounded-lg hover:bg-forest-50" data-id="element-497">
              <ArrowLeft className="w-4 h-4 group-hover:-translate-x-0.5 transition-transform" data-id="element-498" />
              <span className="hidden sm:inline" data-id="element-499">{k.back}</span>
            </button>
            <div className="w-px h-5 bg-forest-100" data-id="element-500" />
            <button onClick={onBack} className="flex items-center" data-id="element-501">
              <LogoFull size={34} data-id="element-502" />
            </button>
          </div>

          <div className="flex items-center gap-2" data-id="element-503">
            {/* Language Toggle */}
            <div className="flex items-center border border-forest-200 rounded-lg overflow-hidden bg-forest-50 text-xs font-semibold" data-id="element-504">
              <button onClick={() => setLang('id')} className={`px-2.5 py-1.5 transition-colors ${lang === 'id' ? 'bg-brand-green text-white' : 'text-forest-600 hover:text-forest-900 hover:bg-forest-100'}`} data-id="element-505">
                ID
              </button>
              <button onClick={() => setLang('en')} className={`px-2.5 py-1.5 transition-colors ${lang === 'en' ? 'bg-brand-green text-white' : 'text-forest-600 hover:text-forest-900 hover:bg-forest-100'}`} data-id="element-506">
                EN
              </button>
            </div>

            <Button onClick={() => window.open('https://wa.me/628127634091?text=Halo%2C+saya+ingin+konsultasi+sawit-net', '_blank')} className="bg-brand-green hover:bg-forest-600 text-white font-semibold h-9 px-4 rounded-lg text-sm shadow-sm hover:shadow-md transition-all" data-id="element-507">
              <span className="hidden sm:inline" data-id="element-508">{k.chatWhatsapp}</span>
              <MessageCircle className="w-4 h-4 sm:hidden" data-id="element-509" />
            </Button>
          </div>
        </div>
      </div>

      <main className="flex-grow" data-id="element-510">
        {/* Hero */}
        <div className="relative bg-brand-green overflow-hidden py-20 md:py-28" data-id="element-511">
          <div className="absolute inset-0 bg-[linear-gradient(to_right,#ffffff04_1px,transparent_1px),linear-gradient(to_bottom,#ffffff04_1px,transparent_1px)] bg-[size:40px_40px] pointer-events-none" data-id="element-512" />
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full max-w-4xl h-px bg-gradient-to-r from-transparent via-brand-gold/60 to-transparent z-[1]" data-id="element-513" />
          <div className="absolute bottom-0 right-0 w-[600px] h-[600px] bg-brand-gold/10 rounded-full blur-3xl pointer-events-none translate-x-1/3 translate-y-1/3 z-[1]" data-id="element-514" />

          <div className="container mx-auto px-6 md:px-8 relative z-10 text-center" data-id="element-515">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-white/20 bg-white/10 backdrop-blur-sm mb-8 shadow-lg" data-id="element-516">
              <span className="w-1.5 h-1.5 rounded-full bg-brand-gold animate-pulse" data-id="element-517" />
              <span className="text-white text-xs font-semibold tracking-wide uppercase" data-id="element-518">
                {k.heroBadge}
              </span>
            </div>
            <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold text-white mb-6 tracking-tight leading-tight font-heading" data-id="element-519">
              {k.heroHeadline1} <br className="hidden md:block" data-id="element-520" />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-gold-300 to-brand-gold" data-id="element-521">
                {k.heroHeadline2}
              </span>
            </h1>
            <p className="text-white/70 text-lg sm:text-xl max-w-2xl mx-auto mb-10 leading-relaxed font-light" data-id="element-522">
              {k.heroSubtitle}
            </p>
          </div>
        </div>

        {/* Contact Options */}
        <section className="py-20 md:py-28 bg-forest-50 relative overflow-hidden" data-id="element-523">
          <div className="absolute top-10 left-0 w-full overflow-hidden pointer-events-none select-none" data-id="element-524">
            <span className="text-[200px] font-bold opacity-[0.04] leading-none absolute -top-20 -left-10 text-forest-500" data-id="element-525">
              01
            </span>
          </div>
          <div className="container mx-auto px-6 md:px-8 relative z-10" data-id="element-526">
            <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-527">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white border border-forest-100 shadow-sm mb-4" data-id="element-528">
                <span className="w-1.5 h-1.5 rounded-full bg-brand-green" data-id="element-529" />
                <span className="text-forest-600 text-xs font-bold tracking-wide uppercase" data-id="element-530">
                  {k.channelsBadge}
                </span>
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-forest-900 mb-4 font-heading" data-id="element-531">
                {k.channelsHeadline}
              </h2>
              <p className="text-forest-600 text-lg" data-id="element-532">{k.channelsSubtitle}</p>
            </div>

            <div className="grid md:grid-cols-3 gap-8" data-id="element-533">
              {/* WhatsApp */}
              <div className="bg-white rounded-2xl p-8 shadow-xl shadow-forest-900/5 border border-forest-100 hover:-translate-y-1 transition-transform duration-300 flex flex-col h-full" data-id="element-534">
                <div className="w-14 h-14 bg-brand-green/10 rounded-2xl flex items-center justify-center text-brand-green mb-6 shadow-sm" data-id="element-535">
                  <MessageCircle className="w-7 h-7" data-id="element-536" />
                </div>
                <h3 className="text-xl font-bold text-forest-900 mb-2 font-heading" data-id="element-537">
                  {k.waTitle}
                </h3>
                <p className="text-forest-500 text-sm mb-6 leading-relaxed" data-id="element-538">
                  {k.waDesc}
                </p>
                <div className="space-y-3 mt-auto" data-id="element-539">
                  {[{
                  number: '+62 812 7634 091',
                  wa: '628127634091'
                }, {
                  number: '+62 822 8787 8772',
                  wa: '6282287878772'
                }, {
                  number: '+62 878 8350 2534',
                  wa: '6287883502534'
                }].map(({
                  number,
                  wa
                }) => <a key={wa} href={`https://wa.me/${wa}?text=Halo%2C+saya+ingin+konsultasi+sawit-net`} target="_blank" rel="noreferrer" className="flex items-center gap-3 px-4 py-3 rounded-xl border border-forest-100 bg-forest-50/50 text-forest-700 font-semibold text-sm hover:bg-brand-green hover:text-white hover:border-brand-green transition-all group" data-id="element-540">
                      <MessageCircle className="w-4 h-4 text-brand-green group-hover:text-white transition-colors" data-id="element-541" />
                      {number}
                    </a>)}
                </div>
              </div>

              {/* Email */}
              <div className="bg-white rounded-2xl p-8 shadow-xl shadow-forest-900/5 border border-forest-100 hover:-translate-y-1 transition-transform duration-300 flex flex-col h-full" data-id="element-542">
                <div className="w-14 h-14 bg-brand-green/10 rounded-2xl flex items-center justify-center text-brand-green mb-6 shadow-sm" data-id="element-543">
                  <Mail className="w-7 h-7" data-id="element-544" />
                </div>
                <h3 className="text-xl font-bold text-forest-900 mb-2 font-heading" data-id="element-545">
                  {k.emailTitle}
                </h3>
                <p className="text-forest-500 text-sm mb-6 leading-relaxed" data-id="element-546">
                  {k.emailDesc}
                </p>

                {/* Use cases */}
                <ul className="space-y-2 mb-6" data-id="element-547">
                  {(lang === 'id' ? ['Penawaran & proposal resmi', 'Pertanyaan teknis mendalam', 'Kerjasama & kemitraan'] : ['Official proposals & quotes', 'In-depth technical questions', 'Partnerships & collaborations']).map(item => <li key={item} className="flex items-center gap-2 text-sm text-forest-600" data-id="element-548">
                      <span className="w-1.5 h-1.5 rounded-full bg-brand-gold flex-shrink-0" data-id="element-549" />
                      {item}
                    </li>)}
                </ul>

                <div className="mt-auto" data-id="element-550">
                  <a href="mailto:layanan@netkrida.co.id" className="flex items-center gap-3 px-4 py-3 rounded-xl border border-forest-100 bg-forest-50/50 text-forest-700 font-semibold text-sm hover:bg-brand-green hover:text-white hover:border-brand-green transition-all group" data-id="element-551">
                    <Mail className="w-4 h-4 text-brand-green group-hover:text-white transition-colors flex-shrink-0" data-id="element-552" />
                    <span className="truncate" data-id="element-553">layanan@netkrida.co.id</span>
                  </a>
                  <p className="text-xs text-forest-400 mt-3 text-center" data-id="element-554">
                    {k.emailNote}
                  </p>
                </div>
              </div>

              {/* Office */}
              <div className="bg-white rounded-2xl p-8 shadow-xl shadow-forest-900/5 border border-forest-100 hover:-translate-y-1 transition-transform duration-300 flex flex-col h-full" data-id="element-555">
                <div className="w-14 h-14 bg-brand-green/10 rounded-2xl flex items-center justify-center text-brand-green mb-6 shadow-sm" data-id="element-556">
                  <Building2 className="w-7 h-7" data-id="element-557" />
                </div>
                <h3 className="text-xl font-bold text-forest-900 mb-2 font-heading" data-id="element-558">
                  {k.officeTitle}
                </h3>
                <p className="text-forest-500 text-sm mb-6 leading-relaxed" data-id="element-559">
                  {k.officeDesc}
                </p>
                <div className="mt-auto space-y-4" data-id="element-560">
                  <div className="flex items-start gap-3 text-sm text-forest-700" data-id="element-561">
                    <MapPin className="w-4 h-4 text-brand-green mt-0.5 flex-shrink-0" data-id="element-562" />
                    <p data-id="element-563">
                      Savana Cluster, A2
                      <br data-id="element-564" />
                      Jl. Karya Bakti, Marpoyan Damai
                      <br data-id="element-565" />
                      Pekanbaru, Riau 28282
                    </p>
                  </div>
                  <div className="flex items-start gap-3 text-sm text-forest-700" data-id="element-566">
                    <Clock className="w-4 h-4 text-brand-green mt-0.5 flex-shrink-0" data-id="element-567" />
                    <div data-id="element-568">
                      <p data-id="element-569">{k.officeHours1}</p>
                      <p data-id="element-570">{k.officeHours2}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-forest-200 to-transparent opacity-50" data-id="element-571" />
        </section>

        {/* Map Section */}
        <section className="py-16 md:py-24 bg-white relative overflow-hidden" data-id="element-572">
          <div className="absolute top-10 left-0 w-full overflow-hidden pointer-events-none select-none" data-id="element-573">
            <span className="text-[200px] font-bold opacity-[0.04] leading-none absolute -top-20 -right-10 text-forest-500" data-id="element-574">
              02
            </span>
          </div>
          <div className="container mx-auto px-6 md:px-8 relative z-10" data-id="element-575">
            <div className="grid md:grid-cols-2 gap-10 lg:gap-20 items-center" data-id="element-576">
              <div data-id="element-577">
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-forest-50 border border-forest-100 shadow-sm mb-4" data-id="element-578">
                  <span className="w-1.5 h-1.5 rounded-full bg-brand-green" data-id="element-579" />
                  <span className="text-forest-600 text-xs font-bold tracking-wide uppercase" data-id="element-580">
                    {k.locationBadge}
                  </span>
                </div>
                <h2 className="text-3xl md:text-4xl font-bold text-forest-900 mb-6 font-heading" data-id="element-581">
                  {k.locationHeadline}
                </h2>
                <p className="text-forest-700/80 text-lg leading-relaxed mb-8" data-id="element-582">
                  {k.locationDesc}
                </p>
                <div className="space-y-6 mb-8" data-id="element-583">
                  <div className="flex gap-4" data-id="element-584">
                    <div className="w-10 h-10 rounded-full bg-forest-50 flex items-center justify-center text-brand-green flex-shrink-0 border border-forest-100" data-id="element-585">
                      <MapPin className="w-5 h-5" data-id="element-586" />
                    </div>
                    <div data-id="element-587">
                      <h4 className="font-bold text-forest-900 mb-1" data-id="element-588">
                        {k.addressLabel}
                      </h4>
                      <p className="text-forest-600 text-sm leading-relaxed" data-id="element-589">
                        Savana Cluster, A2, Jl. Karya Bakti
                        <br data-id="element-590" />
                        Marpoyan Damai, Pekanbaru, Riau 28282
                      </p>
                    </div>
                  </div>
                  <div className="flex gap-4" data-id="element-591">
                    <div className="w-10 h-10 rounded-full bg-forest-50 flex items-center justify-center text-brand-green flex-shrink-0 border border-forest-100" data-id="element-592">
                      <Phone className="w-5 h-5" data-id="element-593" />
                    </div>
                    <div data-id="element-594">
                      <h4 className="font-bold text-forest-900 mb-1" data-id="element-595">
                        {k.phoneLabel}
                      </h4>
                      <p className="text-forest-600 text-sm leading-relaxed" data-id="element-596">
                        +62 812 7634 091 (Sales & Support)
                      </p>
                    </div>
                  </div>
                </div>
                <a href="https://www.google.com/maps/place/NETKRIDA+%7C+PT+NETKRIDA+TUAH+CAKRAWALA/@0.4798741,101.4287554,657m" target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-brand-green text-white font-semibold text-sm hover:bg-forest-600 transition-all shadow-lg shadow-brand-green/20 hover:-translate-y-0.5" data-id="element-597">
                  <MapPin className="w-4 h-4" data-id="element-598" />
                  {k.mapsButton}
                  <ExternalLink className="w-3 h-3 opacity-70" data-id="element-599" />
                </a>
              </div>
              <div className="relative" data-id="element-600">
                <div className="absolute -inset-4 bg-forest-50 rounded-3xl transform rotate-2" data-id="element-601" />
                <div className="relative rounded-2xl overflow-hidden border border-forest-100 shadow-2xl" data-id="element-602">
                  <iframe src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d657.0!2d101.4287554!3d0.4798741!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x31d5aba73d226ded%3A0x8a788b110fe73cd1!2sNETKRIDA%20%7C%20PT%20NETKRIDA%20TUAH%20CAKRAWALA!5e0!3m2!1sid!2sid!4v1708000000000!5m2!1sid!2sid" width="100%" height="450" style={{
                  border: 0,
                  display: 'block'
                }} allowFullScreen loading="lazy" referrerPolicy="no-referrer-when-downgrade" title="Lokasi Kantor NETKRIDA" data-id="element-603" />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Bottom CTA */}
        <section className="py-20 md:py-32 bg-brand-green text-center relative overflow-hidden" data-id="element-604">
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full max-w-4xl h-px bg-gradient-to-r from-transparent via-brand-gold/60 to-transparent z-[1]" data-id="element-605" />
          <div className="absolute inset-0 bg-[linear-gradient(to_right,#ffffff04_1px,transparent_1px),linear-gradient(to_bottom,#ffffff04_1px,transparent_1px)] bg-[size:32px_32px] pointer-events-none z-[1]" data-id="element-606" />
          <div className="absolute bottom-0 right-0 w-[600px] h-[600px] bg-brand-gold/10 rounded-full blur-3xl pointer-events-none translate-x-1/3 translate-y-1/3 z-[1]" data-id="element-607" />
          <div className="container mx-auto px-6 md:px-8 relative z-10" data-id="element-608">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-brand-gold/10 border border-brand-gold/20 mb-6" data-id="element-609">
              <span className="w-1.5 h-1.5 rounded-full bg-brand-gold animate-pulse" data-id="element-610" />
              <span className="text-brand-gold text-xs font-bold tracking-wide uppercase" data-id="element-611">
                {k.ctaBadge}
              </span>
            </div>
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-6 font-heading" data-id="element-612">
              {k.ctaHeadline}
            </h2>
            <p className="text-white/70 text-lg mb-10 max-w-2xl mx-auto leading-relaxed" data-id="element-613">
              {k.ctaSubtitle}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center" data-id="element-614">
              <Button onClick={() => window.open('https://wa.me/628127634091?text=Halo%2C+saya+ingin+konsultasi+gratis+sawit-net', '_blank')} className="h-14 px-10 bg-brand-gold hover:bg-gold-400 text-white font-bold rounded-xl text-lg shadow-xl shadow-brand-gold/20 transition-all hover:scale-105 w-full sm:w-auto" data-id="element-615">
                {k.ctaWhatsapp}
              </Button>
              <Button variant="outline" onClick={onBack} className="h-14 px-10 border-white/30 bg-white/10 text-white hover:bg-white/20 hover:border-white/50 rounded-xl font-semibold text-lg w-full sm:w-auto transition-all" data-id="element-616">
                {k.ctaBack}
              </Button>
            </div>
          </div>
        </section>
      </main>

      <Footer onNavigate={onBack ? () => onBack() : undefined} data-id="element-617" />
      <ScrollToTop data-id="element-618" />
    </div>;
}